import {
    Control,
    Controller,
    FormState,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch,
} from "react-hook-form";
import {
    ActionButton,
    Button,
    Input,
    RadioInput,
    Select,
} from "@jhool-io/fe-components";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    AdditionalClaimInfo,
    EntityCodes,
    IClaimDetailsContent,
    SingleClaim,
} from "../../../types/claims.types";
import { LISTS_DEFAULT_LIMIT } from "../../../../../utils/constants";
import { ProviderCredential } from "../../../../clients/types/clients.types";
import CheckIcon from "../../../../../icons/Check";
import { useChangeClaimsProvider } from "../../../hooks/claims.mutations";
import useToast from "../../../../../hooks/useToast";
import { useFetchProviders } from "../../../../providers/hooks/providers.queries";

interface DiagnosisInfoFormProps {
    control: Control<IClaimDetailsContent>;
    setValue: UseFormSetValue<IClaimDetailsContent>;
    register: UseFormRegister<IClaimDetailsContent>;
    formState: FormState<IClaimDetailsContent>;
    watch: UseFormWatch<IClaimDetailsContent>;
    claimDetails: SingleClaim;
}

type Option = {
    label: React.ReactNode;
    value: boolean;
};

type OptionProvider = {
    label: string;
    value: string;
};

export default function DiagnosisInfoForm({
    control,
    setValue,
    claimDetails,
    register,
    watch,
    formState: { errors },
}: DiagnosisInfoFormProps) {
    const claimContent = claimDetails.claim_content;

    const [hasLabCharge, setHasLabCharge] = useState(() =>
        Boolean(claimContent.outsideLabCharge)
    );

    const [searchParams, setSearchParams] = useSearchParams();

    const [searchString, setSearchString] = useState("");

    const params = useParams();
    const { noteId } = params;

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const {
        isLoading,
        error,
        isSuccess,
        data: providers,
    } = useFetchProviders({
        page: 1,
        limit: LISTS_DEFAULT_LIMIT,
        search_string:
            searchString || claimContent.referringProviderFirstName || "",
    });

    const [selectedProvider, setSelectedProvider] = useState<{
        id: string;
        providerName: string;
    } | null>(null);

    const changeProvider = useChangeClaimsProvider();

    const providersToDisplay = isSuccess
        ? providers?.data?.filter((provider) =>
              import.meta.env.VITE_NODE_ENV === "production"
                  ? provider.credential !== ProviderCredential.INTERN
                  : true
          )
        : [];

    const providersForSelect = isSuccess
        ? providersToDisplay?.map((provider) => ({
              value: provider.provider_id,
              label: `${provider.provider_name}`,
          }))
        : [];

    const diagnosisCodesCount =
        (watch("diagnosisCode1") ? 1 : 0) +
        (watch("diagnosisCode2") ? 1 : 0) +
        (watch("diagnosisCode3") ? 1 : 0) +
        (watch("diagnosisCode4") ? 1 : 0) +
        (watch("diagnosisCode5") ? 1 : 0);

    const entityCodesForSelect = [
        { label: "None", value: "" },
        ...Object.values(EntityCodes).map((code) => {
            const [value] = code.split("-");
            return {
                value,
                label: code,
            };
        }),
    ];

    const resubmissionCodesForSelect = [
        { label: "None", value: "" },
        { label: " 7 (Corrected Claim)", value: "7" },
        { label: "8 (Void/Cancel)", value: "8" },
    ];

    const additionalClaimInfoForSelect = [
        { label: "None", value: "" },
        ...Object.values(AdditionalClaimInfo).map((info) => ({
            label: info,
            value: info,
        })),
    ];

    const handleChangeProvider = async () => {
        const datatoSend = {
            claims_to_update: [
                {
                    claim_id: claimDetails?.claim_id,
                    provider_id: selectedProvider?.id || "",
                },
            ],
        };

        changeProvider.mutate(datatoSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries([
                    "get-claim",
                    { claim_id: claimDetails?.claim_id },
                ]);

                queryClient.invalidateQueries([
                    "get-claim",
                    { note_id: noteId },
                ]);

                toast({
                    mode: "success",
                    message: res.message || "Provider updated successfully",
                });

                searchParams.set("claims_view", "view");
                setSearchParams(searchParams);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err?.response?.data?.message ||
                        "Failed to update provider",
                });
            },
        });
    };

    return (
        <div className="py-[24px] [border-bottom:1px_solid_var(--color-stroke)]">
            <h4 className="text-sm font-semibold mb-[--spacing-header] uppercase md:sticky top-20 z-[4] text-textLink">
                Diagnosis Info
            </h4>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-xs font-semibold leading-tight mb-2">
                        Outside Lab?
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-items]">
                    <RadioInput
                        label="Yes"
                        wrapperClass="min-w-[120px]"
                        checked={hasLabCharge}
                        onChange={() => setHasLabCharge(true)}
                    />

                    <RadioInput
                        label="No"
                        wrapperClass="min-w-[120px]"
                        checked={!hasLabCharge}
                        onChange={() => setHasLabCharge(false)}
                    />

                    {hasLabCharge ? (
                        <div className="w-full">
                            <h6 className="text-textSec text-xs font-medium mb-1">
                                Dollar charges ($)
                            </h6>
                            <div className="flex md:max-w-[40%] gap-[--spacing-items]">
                                <Input
                                    placeholder="00"
                                    {...register("outsideLabCharge")}
                                    hasError={!!errors.outsideLabCharge}
                                    errorText={errors.outsideLabCharge?.message}
                                    showCurrency
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="leading-tight mb-2 text-xs font-semibold">
                        Referring Provider Name
                    </h5>
                </div>

                <div className="flex-[3]">
                    {!error ? (
                        <div className="flex gap-[--spacing-items]">
                            <div className="w-full flex items-center gap-[--spacing-items]">
                                <div className="w-[50%]">
                                    <Select
                                        placeholder=""
                                        options={providersForSelect}
                                        onChange={(val) => {
                                            setSelectedProvider({
                                                id: (val as OptionProvider)
                                                    .value,
                                                providerName: (
                                                    val as OptionProvider
                                                ).label,
                                            });
                                        }}
                                        value={providersForSelect?.find(
                                            (provider) =>
                                                selectedProvider
                                                    ? selectedProvider.providerName.toLowerCase() ===
                                                      provider.label.toLowerCase()
                                                    : `${claimDetails.claim_content.referringProviderLastName} ${claimDetails.claim_content.referringProviderFirstName}`.toLowerCase() ===
                                                      provider.label.toLowerCase()
                                        )}
                                        isSearchable
                                        noOptionsMessage={() =>
                                            isLoading
                                                ? "Loading..."
                                                : "No options"
                                        }
                                        onInputChange={(value) => {
                                            setSearchString(value);
                                        }}
                                        menuIsOpen={searchString.length > 0}
                                        filterOption={() => true}
                                    />
                                </div>
                                <div>
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        actionTitle="Save selection"
                                        onClick={handleChangeProvider}
                                        disabled={
                                            changeProvider.isLoading ||
                                            !selectedProvider
                                        }
                                    >
                                        <CheckIcon className="stroke-primary size-5" />
                                    </ActionButton>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {error ? (
                        <p>{`${claimContent?.referringProviderLastName} ${claimContent?.referringProviderFirstName}`}</p>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-xs font-semibold leading-tight mb-2">
                        Entity Code
                    </h5>
                </div>

                <div className="flex-[3] w-1/4">
                    <Controller
                        name="referringProviderNumQual"
                        control={control}
                        defaultValue={
                            entityCodesForSelect?.find(
                                (code) =>
                                    claimDetails.claim_content
                                        .referringProviderNumQual === code.value
                            )?.value
                        }
                        render={({ field }) => (
                            <Select
                                placeholder="Entity Code"
                                options={entityCodesForSelect}
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                defaultValue={entityCodesForSelect?.find(
                                    (code) =>
                                        claimDetails.claim_content
                                            .referringProviderNumQual ===
                                        code.value
                                )}
                                isSearchable
                            />
                        )}
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-xs font-semibold leading-tight mb-2">
                        Referring Provider NPI
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-items]">
                    <Input
                        placeholder="NPI"
                        className="w-[100%]"
                        {...register("referringProviderNPI")}
                        hasError={!!errors.referringProviderNPI}
                        errorText={errors.referringProviderNPI?.message}
                        defaultValue={claimContent.referringProviderNPI ?? ""}
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-xs font-semibold leading-tight mb-2">
                        Additional Claim Info (Box 19)
                    </h5>
                </div>

                <div className="flex-[3]">
                    <Controller
                        name="additionalClaimInfo"
                        control={control}
                        defaultValue={
                            additionalClaimInfoForSelect.find(
                                (info) =>
                                    claimContent.additionalClaimInfo ===
                                    info.value
                            )?.value
                        }
                        render={({ field }) => (
                            <Select
                                placeholder="Additional Claim Info"
                                options={additionalClaimInfoForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as OptionProvider).value
                                    )
                                }
                                defaultValue={additionalClaimInfoForSelect.find(
                                    (info) =>
                                        claimContent.additionalClaimInfo ===
                                        info.value
                                )}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-xs font-semibold leading-tight mb-2">
                        Resubmission Code
                    </h5>
                </div>

                <div className="flex-[3] w-1/4">
                    <Controller
                        name="resubmissionCode"
                        control={control}
                        defaultValue={
                            resubmissionCodesForSelect.find(
                                (code) =>
                                    claimDetails.claim_content
                                        .resubmissionCode === code.value
                            )?.value
                        }
                        render={({ field }) => (
                            <Select
                                placeholder="Resubmission Code"
                                options={resubmissionCodesForSelect}
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                defaultValue={resubmissionCodesForSelect.find(
                                    (code) =>
                                        claimDetails.claim_content
                                            .resubmissionCode === code.value
                                )}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-xs font-semibold leading-tight mb-2">
                        Original Ref No
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-items]">
                    <Input
                        placeholder="Original Ref No"
                        className="md:max-w-[40%]"
                        {...register("originalClaimReferenceNumber")}
                        hasError={!!errors.originalClaimReferenceNumber}
                        errorText={errors.originalClaimReferenceNumber?.message}
                        defaultValue={
                            claimContent.originalClaimReferenceNumber ?? ""
                        }
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-xs font-semibold leading-tight mb-2">
                        Diagnosis or Nature of Illness or Injury
                    </h5>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-wrap gap-[--spacing-items] md:max-w-[80%]">
                        <div className="w-full flex flex-col md:flex-row md:items-center gap-[--spacing-items]">
                            <span className="text-xs font-semibold">A</span>
                            <Input
                                placeholder="F41.1  (Anxiety Disorder Unspecified)"
                                {...register("diagnosisCode1")}
                                hasError={!!errors.diagnosisCode1}
                                errorText={errors.diagnosisCode1?.message}
                                defaultValue={claimContent.diagnosisCode1 ?? ""}
                            />
                            <Button
                                variant="normal"
                                disabled
                                className="flex items-center gap-[--spacing-items] border border-strokedark text-xs hover:bg-strokedark"
                            >
                                <span>-</span> Remove
                            </Button>
                        </div>

                        {/* code 2 */}
                        {watch("diagnosisCode2") ? (
                            <div className="w-full flex flex-col md:flex-row md:items-center gap-[--spacing-items]">
                                <span className="text-xs font-semibold">B</span>
                                <Input
                                    placeholder="F90.9  (ADHD Unspecified type)"
                                    {...register("diagnosisCode2")}
                                    hasError={!!errors.diagnosisCode2}
                                    errorText={errors.diagnosisCode2?.message}
                                    defaultValue={claimContent.diagnosisCode2}
                                />
                                <Button
                                    variant="normal"
                                    className="flex items-center gap-[--spacing-items] border border-strokedark text-xs hover:bg-strokedark focus:bg-strokedark"
                                    onClick={() =>
                                        setValue("diagnosisCode2", "")
                                    }
                                >
                                    <span>-</span> Remove
                                </Button>
                            </div>
                        ) : null}

                        {/* code 3 */}
                        {watch("diagnosisCode3") ? (
                            <div className="w-full flex flex-col md:flex-row md:items-center gap-[--spacing-items]">
                                <span className="text-xs font-semibold">C</span>
                                <Input
                                    placeholder="F90.9  (ADHD Unspecified type)"
                                    {...register("diagnosisCode3")}
                                    hasError={!!errors.diagnosisCode3}
                                    errorText={errors.diagnosisCode3?.message}
                                    defaultValue={claimContent.diagnosisCode3}
                                />
                                <Button
                                    variant="normal"
                                    className="flex items-center gap-[--spacing-items] border border-strokedark text-xs hover:bg-strokedark focus:bg-strokedark"
                                    onClick={() =>
                                        setValue("diagnosisCode3", "")
                                    }
                                >
                                    <span>-</span> Remove
                                </Button>
                            </div>
                        ) : null}

                        {/* code 4 */}
                        {watch("diagnosisCode4") ? (
                            <div className="w-full flex flex-col md:flex-row md:items-center gap-[--spacing-items]">
                                <span className="text-xs font-semibold">D</span>
                                <Input
                                    placeholder="F90.9  (ADHD Unspecified type)"
                                    {...register("diagnosisCode4")}
                                    hasError={!!errors.diagnosisCode4}
                                    errorText={errors.diagnosisCode4?.message}
                                    defaultValue={claimContent.diagnosisCode4}
                                />
                                <Button
                                    variant="normal"
                                    className="flex items-center gap-[--spacing-items] border border-strokedark text-xs hover:bg-strokedark focus:bg-strokedark"
                                    onClick={() =>
                                        setValue("diagnosisCode4", "")
                                    }
                                >
                                    <span>-</span> Remove
                                </Button>
                            </div>
                        ) : null}

                        {/* code 5 */}
                        {watch("diagnosisCode5") ? (
                            <div className="w-full flex flex-col md:flex-row md:items-center gap-[--spacing-items]">
                                <span className="text-xs font-semibold">E</span>
                                <Input
                                    placeholder="F90.9  (ADHD Unspecified type)"
                                    {...register("diagnosisCode5")}
                                    hasError={!!errors.diagnosisCode5}
                                    errorText={errors.diagnosisCode5?.message}
                                    defaultValue={claimContent.diagnosisCode5}
                                />
                                <Button
                                    variant="normal"
                                    className="flex items-center gap-[--spacing-items] border border-strokedark text-xs hover:bg-strokedark focus:bg-strokedark"
                                    onClick={() =>
                                        setValue("diagnosisCode5", "")
                                    }
                                >
                                    <span>-</span> Remove
                                </Button>
                            </div>
                        ) : null}

                        <Button
                            className="flex items-center gap-2 ml-auto text-xs"
                            disabled={diagnosisCodesCount === 5}
                            onClick={() =>
                                setValue(
                                    `diagnosisCode${
                                        diagnosisCodesCount + 1
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    }` as any,
                                    " "
                                )
                            }
                        >
                            <span>+</span> Add new
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
